import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

interface IActiveMicIndicatorProps {
  stream: MediaStream | null;
}

type MicHealth = 'active' | 'silent' | 'muted' | 'ended' | 'no-track';

const LEVEL_BARS = 5;
const SILENCE_THRESHOLD = 5;
const SAMPLE_INTERVAL_MS = 100;
// If the mic reads below the silence threshold for this many consecutive samples, flag it
const SILENCE_CONSECUTIVE_LIMIT = 30; // ~3 seconds

const ActiveMicIndicator: React.FC<IActiveMicIndicatorProps> = ({ stream }) => {
  const [levels, setLevels] = useState<number[]>(new Array(LEVEL_BARS).fill(0));
  const [deviceLabel, setDeviceLabel] = useState<string>('');
  const [health, setHealth] = useState<MicHealth>('no-track');
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const silenceCountRef = useRef(0);

  useEffect(() => {
    if (!stream) {
      setHealth('no-track');
      setDeviceLabel('');
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      setHealth('no-track');
      setDeviceLabel('');
      return;
    }

    const track = audioTracks[0];

    // Get the device label from the track
    setDeviceLabel(track.label || 'Unknown microphone');

    // Set initial health based on track state
    const updateHealthFromTrack = () => {
      if (track.readyState === 'ended') {
        setHealth('ended');
      } else if (track.muted || !track.enabled) {
        setHealth('muted');
      } else {
        setHealth('active');
      }
    };
    updateHealthFromTrack();

    // Listen for track state changes
    const handleEnded = () => setHealth('ended');
    const handleMute = () => setHealth('muted');
    const handleUnmute = () => setHealth('active');

    track.addEventListener('ended', handleEnded);
    track.addEventListener('mute', handleMute);
    track.addEventListener('unmute', handleUnmute);

    // Set up audio analyser for level visualization
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 32;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
      silenceCountRef.current = 0;

      const updateLevels = () => {
        if (!isMounted || !analyserRef.current || !dataArrayRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        // Calculate average level from all frequency bins
        const sum = dataArrayRef.current.reduce((a, b) => a + b, 0);
        const avg = sum / dataArrayRef.current.length;

        // Track consecutive silence
        if (avg < SILENCE_THRESHOLD) {
          silenceCountRef.current++;
          if (
            silenceCountRef.current >= SILENCE_CONSECUTIVE_LIMIT &&
            track.readyState === 'live' &&
            track.enabled &&
            !track.muted
          ) {
            setHealth('silent');
          }
        } else {
          silenceCountRef.current = 0;
          if (track.readyState === 'live' && track.enabled && !track.muted) {
            setHealth('active');
          }
        }

        // Sample a few frequency bins to create bar levels
        const step = Math.floor(dataArrayRef.current.length / LEVEL_BARS);
        const newLevels = Array.from({ length: LEVEL_BARS }, (_, i) => {
          const idx = Math.min(i * step, dataArrayRef.current!.length - 1);
          return dataArrayRef.current![idx];
        });
        setLevels(newLevels);

        timeoutId = setTimeout(updateLevels, SAMPLE_INTERVAL_MS);
      };

      updateLevels();
    } catch (err) {
      console.warn(
        'ActiveMicIndicator: Failed to initialize audio analyser:',
        err,
      );
    }

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      track.removeEventListener('ended', handleEnded);
      track.removeEventListener('mute', handleMute);
      track.removeEventListener('unmute', handleUnmute);
      if (audioContextRef.current) {
        audioContextRef.current.close().catch((err) => {
          console.warn(
            'ActiveMicIndicator: Failed to close AudioContext:',
            err,
          );
        });
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      dataArrayRef.current = null;
    };
  }, [stream]);

  const healthConfig: Record<
    MicHealth,
    { color: string; label: string; icon: 'on' | 'off' }
  > = {
    active: { color: '#4CAF50', label: 'Mic active', icon: 'on' },
    silent: { color: '#FF9800', label: 'No audio detected', icon: 'on' },
    muted: { color: '#f44336', label: 'Mic muted', icon: 'off' },
    ended: { color: '#f44336', label: 'Mic disconnected', icon: 'off' },
    'no-track': { color: '#9E9E9E', label: 'No microphone', icon: 'off' },
  };

  const { color, label, icon } = healthConfig[health];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        borderRadius: '8px',
        backgroundColor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.2)}`,
        minWidth: 0,
      }}
    >
      {/* Mic icon with health color */}
      {icon === 'on' ? (
        <MicIcon sx={{ fontSize: '18px', color, flexShrink: 0 }} />
      ) : (
        <MicOffIcon sx={{ fontSize: '18px', color, flexShrink: 0 }} />
      )}

      {/* Level bars */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          height: '16px',
          flexShrink: 0,
        }}
      >
        {levels.map((level, i) => {
          const isIdle = level < SILENCE_THRESHOLD;
          const barHeight = isIdle ? 3 : Math.max(3, (level / 255) * 16);
          return (
            <Box
              key={i}
              sx={{
                width: '3px',
                height: `${barHeight}px`,
                backgroundColor:
                  health === 'active' ? color : alpha(color, 0.4),
                borderRadius: '1.5px',
                transition: 'height 0.1s ease-out',
              }}
            />
          );
        })}
      </Box>

      {/* Status text + device name */}
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        {deviceLabel && (
          <Typography
            sx={{
              fontSize: '10px',
              color: 'text.secondary',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '180px',
            }}
          >
            {deviceLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ActiveMicIndicator;
